import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ApiService } from '../service/api.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from '../loader/loader.service';
import { whitespace } from '../common/whitespace';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { APP_DATE_FORMATS } from '../service/date.adapter';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';

const DEFAULT_MAINTENANCE_MESSAGE =
`Scheduled Server Maintenance Alert

Dear Users, Please be informed that we will be performing essential server maintenance during the time below. Our services will be temporarily unavailable.

Date: [Date]

Time: [Start Time] to [End Time]
⏱️ Duration: Approximately [Duration]

We apologize for any inconvenience and appreciate your patience and understanding.`;

@Component({
    selector: 'app-maintenance',
    templateUrl: './maintenance.component.html',
    styleUrls: ['./maintenance.component.css'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: APP_DATE_FORMATS,
        },
    ],
    standalone: false
})

export class MaintenanceComponent implements OnInit, OnDestroy {
  private refreshTimer: any;
  @ViewChild('maintenanceDialog') maintenanceDialog: TemplateRef<any>;

  maintenanceForm: FormGroup;
  today = new Date();

  maintenanceList: any[] = [];
  gridData: GridDataResult;
  pageSize = 10;
  skip = 0;
  sort: SortDescriptor[] = [{ field: 'Id', dir: 'desc' }];
  public pageableWithAll: any = { pageSizes: [5, 10, 20, 50, 100, 'All'] };

  dialogMode: 'add' | 'view' | 'edit' = 'add';
  selectedItem: any = null;
  isEditMode = false;
  editMaintenanceId: number | null = null;
  private dialogRef: MatDialogRef<any>;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly _http: ApiService,
    private readonly toaster: ToastrService,
    private readonly loaderService: LoaderService,
    private readonly dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.getMaintenanceList();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    clearInterval(this.refreshTimer);
  }

  startAutoRefresh(): void {
    this.refreshTimer = setInterval(() => {
      // Re-slice the existing list so isActiveNow() re-evaluates with current time
      this.refreshGrid();

      // If edit dialog is open and toggle is active, auto-flip it when schedule ends
      if (this.dialogMode === 'edit' && this.maintenanceForm?.get('isMaintenance')?.value) {
        const endDateVal = this.maintenanceForm.get('EndDate')?.value;
        const endTimeVal = this.maintenanceForm.get('endTime')?.value;
        if (endDateVal && endTimeVal) {
          const end = new Date(endDateVal);
          const t = new Date(endTimeVal);
          end.setHours(t.getHours(), t.getMinutes(), t.getSeconds(), 0);
          if (new Date() > end) {
            this.maintenanceForm.patchValue({ isMaintenance: false });
          }
        }
      }
    }, 60000);
  }

  buildForm() {
    this.maintenanceForm = this.formBuilder.group({
      message: ['', Validators.required, null, whitespace],
      StartDate: ['', [Validators.required, this.S_notPastDateValidator()]],
      EndDate: ['', [Validators.required, this.E_notPastDateValidator()]],
      startTime: ['', [Validators.required]],
      endTime: ['', [Validators.required]],
      isMaintenance: [false],
    });
  }

  getMaintenanceList() {
    this.loaderService.show();
    this._http.getMaintenance().subscribe((res: any) => {
      this.loaderService.hide();
      if (res.Success) {
        this.maintenanceList = res.Data || [];
        this.refreshGrid();
      }
    });
  }

  refreshGrid() {
    const listWithStatus = this.maintenanceList.map(item => ({
      ...item,
      IsActiveNow: this.isActiveNow(item),
    }));
    const sorted = orderBy(listWithStatus, this.sort);
    const effectivePageSize = (!this.pageSize || this.pageSize <= 0) ? this.maintenanceList.length : this.pageSize;
    this.gridData = {
      data: sorted.slice(this.skip, this.skip + effectivePageSize),
      total: this.maintenanceList.length,
    };
  }

  pageChange(event: any) {
    this.skip = event.skip ?? 0;
    const take = event.take;
    this.pageSize = (take > 0) ? take : this.maintenanceList.length;
    this.refreshGrid();
  }

  sortChange(sort: SortDescriptor[]) {
    this.sort = sort;
    this.refreshGrid();
  }

  openAddDialog() {
    this.isEditMode = false;
    this.dialogMode = 'add';
    this.selectedItem = null;
    this.editMaintenanceId = null;
    this.buildForm();
    const today = new Date();
    this.maintenanceForm.patchValue({
      message: DEFAULT_MAINTENANCE_MESSAGE,
      StartDate: today,
      EndDate: today,
    });
    this.dialogRef = this.dialog.open(this.maintenanceDialog, {
      autoFocus: false,
      disableClose: true,
      width: '750px',
    });
  }

  canEdit(item: any): boolean {
    const endDate = new Date(item.EndDate);
    endDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate >= today;
  }

  openEditDialog(item: any) {
    this.isEditMode = true;
    this.dialogMode = 'edit';
    this.selectedItem = item;
    this.editMaintenanceId = item.Id;
    this.buildForm();

    const startTimeValue = new Date();
    const [sh, sm, ss] = item.StartTime.split(':');
    startTimeValue.setHours(Number(sh), Number(sm), Number(ss || 0));

    const endTimeValue = new Date();
    const [eh, em, es] = item.EndTime.split(':');
    endTimeValue.setHours(Number(eh), Number(em), Number(es || 0));

    this.maintenanceForm.patchValue({
      message: item.MessageBox,
      StartDate: item.StartDate,
      EndDate: item.EndDate,
      startTime: startTimeValue,
      endTime: endTimeValue,
      isMaintenance: this.isActiveNow(item),
    });

    this.dialogRef = this.dialog.open(this.maintenanceDialog, {
      autoFocus: false,
      disableClose: true,
      width: '750px',
    });
  }

  openViewDialog(item: any) {
    this.dialogMode = 'view';
    this.selectedItem = item;
    this.dialogRef = this.dialog.open(this.maintenanceDialog, {
      autoFocus: false,
      disableClose: true,
      width: '600px',
    });
  }

  closeDialog() {
    this.dialogRef?.close();
  }

  S_notPastDateValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) return null;

      if (!this.isEditMode) {
        const inputDate = new Date(control.value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        inputDate.setHours(0, 0, 0, 0);
        if (inputDate < today) return { pastDate: true };
      }

      const start = this.maintenanceForm?.get('StartDate')?.value;
      const end = this.maintenanceForm?.get('EndDate')?.value;
      if (!start || !end) return null;

      const startDate = new Date(start);
      const endDate = new Date(end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < startDate) {
        this.maintenanceForm?.get('EndDate')?.setValue(start);
        return { endBeforeStart: true };
      }
      return null;
    };
  }

  E_notPastDateValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) return null;

      const inputDate = new Date(control.value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      inputDate.setHours(0, 0, 0, 0);

      if (inputDate < today) return { pastDate: true };

      const start = this.maintenanceForm?.get('StartDate')?.value;
      const end = this.maintenanceForm?.get('EndDate')?.value;
      if (!start || !end) return null;

      const startDate = new Date(start);
      const endDate = new Date(end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < startDate) return { endBeforeStart: true };
      return null;
    };
  }

  submit(_form: FormGroup) {
    _form.get('StartDate')?.updateValueAndValidity();
    _form.get('EndDate')?.updateValueAndValidity();

    if (_form.invalid) {
      _form.get('StartDate')?.markAsTouched();
      _form.get('EndDate')?.markAsTouched();
      _form.markAllAsTouched();
      return;
    }

    const startTime = _form.controls['startTime']?.value;
    const endTime = _form.controls['endTime']?.value;
    if (
      _form.value.endTime.setHours(new Date(endTime).getHours(), new Date(endTime).getMinutes(), 0, 0) === new Date().setHours(0, 0, 0, 0) ||
      _form.value.startTime.setHours(new Date(startTime).getHours(), new Date(startTime).getMinutes(), 0, 0) === new Date().setHours(0, 0, 0, 0)
    ) {
      this.toaster.error('Please select Valid Time');
      return;
    }

    const starttime = _form.value.startTime;
    const endtime = _form.value.endTime;
    const start = _form.get('StartDate')?.value;
    const end = _form.get('EndDate')?.value;
    const startDate = new Date(start);
    const endDate = new Date(end);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (endDate.getDate() === startDate.getDate()) {
      if (starttime > endtime) {
        this.toaster.error('End time must be greater than start time');
        return;
      }
    }

    this.loaderService.show();
    const obj: any = {
      StartDate: this.setNewDateValue(_form.value.StartDate),
      EndDate: this.setNewDateValue(_form.value.EndDate),
      StartTime: _form.value.startTime.toLocaleTimeString('en-US', { hour12: false }),
      EndTime: _form.value.endTime.toLocaleTimeString('en-US', { hour12: false }),
      MessageBox: _form.value.message,
      IsMaintenance: _form.value.isMaintenance,
    };

    if (this.isEditMode) {
      obj.Id = this.editMaintenanceId;
      this._http.updateMaintenance(obj).subscribe((res: any) => {
        this.loaderService.hide();
        if (res.Success) {
          this.toaster.success('Maintenance updated successfully');
          this.closeDialog();
          this.getMaintenanceList();
        } else {
          this.toaster.error('Something went wrong');
        }
      });
    } else {
      this._http.addMaintenance(obj).subscribe((res: any) => {
        this.loaderService.hide();
        if (res.Success) {
          this.toaster.success('Maintenance added successfully');
          this.closeDialog();
          this.getMaintenanceList();
        } else {
          this.toaster.error('Something went wrong');
        }
      });
    }
  }

  setNewDateValue(date: any) {
    const tzoffset = new Date(date).getTimezoneOffset() * 60000;
    if (date._d) {
      return new Date(date - tzoffset).toISOString().slice(0, -1);
    }
    if (typeof date === 'string' && date.split('T')[1] !== '00:00:00') {
      return new Date(new Date(date).getTime() - tzoffset).toISOString().slice(0, -1);
    }
    return date;
  }

  formatTime(timeStr: string): string {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    return `${parts[0]}:${parts[1]}`;
  }

  openDialogRefforactive(event: any) {
    this.maintenanceForm.controls['isMaintenance'].setValue(event.checked);
  }

  isActiveNow(item: any): boolean {
    if (!item.IsMaintenance) return false;
    const now = new Date();
    const endDate = new Date(item.EndDate);
    const [eh, em, es] = item.EndTime.split(':');
    endDate.setHours(Number(eh), Number(em), Number(es || 0), 0);
    return now <= endDate;
  }
}
